import { Api, buildFormData } from '@deepcloud/api-base'
import { mergeConfig } from '@deepcloud/http-client'
import { withQueryParams } from '../../../shared'
import type {
  HttpClient,
  HttpClientRequestConfig,
} from '@deepcloud/http-client'
import type {
  DeclineDeleteRequest,
  KbAddEmployeeGroupToArticleParams,
  KbAddEmployeeGroupToArticleResponse,
  KbAddNextEditorParams,
  KbAddNextEditorResponse,
  KBArticleListResponse,
  KBArticleResponse,
  KbAutosaveArticleFormData,
  KbAutosaveArticleResponse,
  KBAutosaveListResponse,
  KBAutosaveResponse,
  KbDeclineArticleDeleteParams,
  KbDeclineArticleDeleteResponse,
  KbDeleteArticleParams,
  KbDeleteArticleResponse,
  KbDeleteAutosaveResponse,
  KbDeleteTreeNodeParams,
  KbDeleteTreeNodeResponse,
  KbExportArticlePdfParams,
  KbGetArticleParams,
  KbGetArticlePreviewFormData,
  KbGetArticlesParams,
  KbGetAutosaveListParams,
  KbGetTreeParams,
  KbGetTreePathResponse,
  KbMoveTreeNodeParams,
  KbMoveTreeNodeResponse,
  KbPostTreeNodeParams,
  KbPostTreeNodeResponse,
  KbRejectDraftResponse,
  KbRenameTreeNodeResponse,
  KbRestoreArticleParams,
  KbRestoreArticleResponse,
  KbRetractArticleParams,
  KbRetractArticleResponse,
  KbSaveArticleFormData,
  KbSaveArticleResponse,
  KbSaveTranslationFormData,
  KbSearchTreeParams,
  KBTreeNode,
  KBTreeSearchResponse,
  KbUnwatchArticleParams,
  KbUnwatchArticleResponse,
  KbWatchArticleParams,
  KbWatchArticleResponse,
  MoveNode,
  NodeCreate,
  RejectRequest,
  RenameNode,
} from '../../@types'

export class KbAPI extends Api {
  constructor(httpClient: HttpClient) {
    super(httpClient)
  }

  /**
   * Get Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getArticle(
    kbId: string,
    params?: KbGetArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/${kbId}`

    return this.httpClient.get<KBArticleResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Kb Articles
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getArticles(
    params?: KbGetArticlesParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/articles`

    return this.httpClient.get<KBArticleListResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Export Kb Article Pdf
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async exportArticlePdf(
    kbId: string,
    params?: KbExportArticlePdfParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/${kbId}/pdf`

    return this.httpClient.get<Blob>(
      path,
      mergeConfig({ params, responseType: 'blob' }, config),
    )
  }

  /**
   * Watch Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async watchArticle(
    params: KbWatchArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/watch`, params)

    return this.httpClient.post<KbWatchArticleResponse>(path, undefined, config)
  }

  /**
   * Unwatch Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async unwatchArticle(
    params: KbUnwatchArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/unwatch`, params)

    return this.httpClient.post<KbUnwatchArticleResponse>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Get Kb Article Revision
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getArticleRevision(
    kbId: string,
    revision: string,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/${kbId}/${revision}`

    return this.httpClient.get<KBArticleResponse>(path, config)
  }

  /**
   * Save Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async saveArticle(
    payload: KbSaveArticleFormData,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/save`

    return this.httpClient.post<KbSaveArticleResponse>(
      path,
      buildFormData(payload),
      config,
    )
  }

  /**
   * Get Kb Article Preview
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getArticlePreview(
    payload: KbGetArticlePreviewFormData,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/preview`

    return this.httpClient.post<KBArticleResponse>(
      path,
      buildFormData(payload),
      config,
    )
  }

  /**
   * Delete Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteArticle(
    params: KbDeleteArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/delete`, params)

    return this.httpClient.delete<KbDeleteArticleResponse>(path, config)
  }

  /**
   * Retract Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async retractArticle(
    params: KbRetractArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/retract`, params)

    return this.httpClient.post<KbRetractArticleResponse>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Decline Article Delete
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async declineArticleDelete(
    params: KbDeclineArticleDeleteParams,
    payload?: DeclineDeleteRequest | null,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/decline_delete`, params)

    return this.httpClient.post<KbDeclineArticleDeleteResponse>(
      path,
      payload,
      config,
    )
  }

  /**
   * Restore Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async restoreArticle(
    params: KbRestoreArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/restore`, params)

    return this.httpClient.post<KbRestoreArticleResponse>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Add Next Editor
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async addNextEditor(
    params: KbAddNextEditorParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/next_editor`, params)

    return this.httpClient.post<KbAddNextEditorResponse>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Save Kb Translation
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async saveTranslation(
    payload: KbSaveTranslationFormData,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/translation`

    return this.httpClient.post<unknown>(path, buildFormData(payload), config)
  }

  /**
   * Add Employee Group To Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async addEmployeeGroupToArticle(
    params: KbAddEmployeeGroupToArticleParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/article/employeegroup`, params)

    return this.httpClient.post<KbAddEmployeeGroupToArticleResponse>(
      path,
      undefined,
      config,
    )
  }

  /**
   * Reject Draft
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async rejectDraft(
    kbId: string,
    payload?: RejectRequest | null,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/article/${kbId}/reject`

    return this.httpClient.post<KbRejectDraftResponse>(path, payload, config)
  }

  /**
   * Get Kb Tree
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getTree(params?: KbGetTreeParams, config?: HttpClientRequestConfig) {
    const path = `/kb/tree`

    return this.httpClient.get<KBTreeNode[]>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Search Kb Tree
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async searchTree(
    params: KbSearchTreeParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/tree/search`

    return this.httpClient.get<KBTreeSearchResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }

  /**
   * Get Kb Tree Path
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getTreePath(treeId: number, config?: HttpClientRequestConfig) {
    const path = `/kb/treepath/${treeId}`

    return this.httpClient.get<KbGetTreePathResponse>(path, config)
  }

  /**
   * Post Tree Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async postTreeNode(
    payload: NodeCreate,
    params?: KbPostTreeNodeParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/tree/node`, params)

    return this.httpClient.post<KbPostTreeNodeResponse>(path, payload, config)
  }

  /**
   * Delete Tree Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteTreeNode(
    params: KbDeleteTreeNodeParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/tree/node/delete`, params)

    return this.httpClient.delete<KbDeleteTreeNodeResponse>(path, config)
  }

  /**
   * Rename Tree Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async renameTreeNode(
    treeId: number,
    payload: RenameNode,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/tree/rename/${treeId}`

    return this.httpClient.post<KbRenameTreeNodeResponse>(path, payload, config)
  }

  /**
   * Move Tree Node
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async moveTreeNode(
    treeId: number,
    payload: MoveNode,
    params?: KbMoveTreeNodeParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = withQueryParams(`/kb/tree/move/${treeId}`, params)

    return this.httpClient.post<KbMoveTreeNodeResponse>(path, payload, config)
  }

  /**
   * Init Autosave Endpoint
   * -------------------
   * Errors:
   * - none documented
   */
  async initAutosave(config?: HttpClientRequestConfig) {
    const path = `/kb/autosave/get-new-id`

    return this.httpClient.post<unknown>(path, undefined, config)
  }

  /**
   * Autosave Kb Article
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async autosaveArticle(
    payload: KbAutosaveArticleFormData,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/autosave`

    return this.httpClient.post<KbAutosaveArticleResponse>(
      path,
      buildFormData(payload),
      config,
    )
  }

  /**
   * Get Autosave
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getAutosave(autosaveId: number, config?: HttpClientRequestConfig) {
    const path = `/kb/autosave/${autosaveId}`

    return this.httpClient.get<KBAutosaveResponse>(path, config)
  }

  /**
   * Delete Autosave
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async deleteAutosave(autosaveId: number, config?: HttpClientRequestConfig) {
    const path = `/kb/autosave/${autosaveId}`

    return this.httpClient.delete<KbDeleteAutosaveResponse>(path, config)
  }

  /**
   * Get Autosave List
   * -------------------
   * Errors:
   * - 422 Validation Error → {@link HTTPValidationError}
   */
  async getAutosaveList(
    params?: KbGetAutosaveListParams,
    config?: HttpClientRequestConfig,
  ) {
    const path = `/kb/autosaves`

    return this.httpClient.get<KBAutosaveListResponse>(
      path,
      mergeConfig({ params }, config),
    )
  }
}
